/**
 * @fileoverview Shared recharts styling constants.
 * @description Grid stroke / axis tick / tooltip / palette colors used across every recharts-based
 * chart, factored out of the hardcoded-per-component hex values every existing sales chart
 * currently repeats (confirmed no such shared file existed before this one). recharts style props
 * (`stroke`, `fill`, tick `fill`, etc.) take literal color strings, not Tailwind classes, so these
 * stay plain hex constants rather than CSS custom properties — the ones with a design-token
 * equivalent are kept numerically identical to `tokens/colors.css` (noted per constant) so a chart
 * and its surrounding UI never drift apart; the rest (a lighter accent line tone, a muted
 * axis/label slate) have no existing token equivalent and are chart-only.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

export const CHART_ACCENT = '#102a5c' // matches --color-accent
export const CHART_PRIMARY = '#2563eb' // matches --color-primary
export const CHART_PRIMARY_LIGHT = '#93c5fd' // lighter accent-line tone, chart-only
export const CHART_SLATE = '#64748b' // muted axis/label tone, chart-only
export const CHART_SUCCESS = '#22c55e' // matches --color-success
export const CHART_WARNING = '#f59e0b' // matches --color-warning
export const CHART_ERROR = '#ef4444' // matches --color-error
export const CHART_GRID = '#e5e7eb' // matches --color-border
export const CHART_TEXT = '#111827' // matches --color-text

/**
 * @description Grades a ratio into success/warning/error by how far past two thresholds it sits —
 * e.g. an NPA% or utilization ratio where "good/watch/bad" bands matter more than the exact value.
 * @param {number} pct - The ratio to grade (same scale as `thresholds`).
 * @param {[number, number]} [thresholds] - `[warningAt, errorAt]`. Defaults to `[0.5, 1.2]`.
 * @returns {string} `CHART_ERROR` at/above the second threshold, `CHART_WARNING` at/above the
 * first, else `CHART_SUCCESS`.
 */
export function severityColor(pct: number, thresholds: [number, number] = [0.5, 1.2]): string {
  if (pct >= thresholds[1]) return CHART_ERROR
  if (pct >= thresholds[0]) return CHART_WARNING
  return CHART_SUCCESS
}

// Multi-hue categorical palette for any N-way categorical breakdown (pie/donut charts, ranked
// bars, category columns) — every color sits at roughly the same saturation/lightness, so no
// segment reads as more or less important purely from how dark its shade is; the chart's own
// wedge size or bar length already shows magnitude, color only needs to distinguish categories.
export const CATEGORICAL_PALETTE = [
  '#4caf7d', // green — the majority/largest segment leads with this
  '#6c8ee3', // blue
  '#f2a65a', // orange
  '#a47fd1', // purple
  '#e2636b', // coral red
  '#4fbdae', // teal
  '#e8c94a', // gold
  '#e389b9', // pink
  '#c97b5a', // terracotta
  '#7c93a8', // slate
]

/**
 * @description Returns the first `n` colors of `CATEGORICAL_PALETTE`, cycling if `n` exceeds it.
 * @param {number} n - How many categories need a color.
 * @returns {string[]} `n` hex color strings.
 */
export function categoricalPalette(n: number): string[] {
  return Array.from(
    { length: n },
    (_, index) => CATEGORICAL_PALETTE[index % CATEGORICAL_PALETTE.length]!,
  )
}
